import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { useRef } from "react";

const Billing = () => {
  const toastRef = useRef(null);

  const [currentView, setCurrentView] = useState("grid");
  const [globalFilter, setGlobalFilter] = useState("");
  const [invoiceItems, setInvoiceItems] = useState([
    { id: 1, productName: "", qty: 0, rate: 0, discount: 0, total: 0 },
  ]);
  const [paymentType, setPaymentType] = useState("Cash");
  const [roundOff, setRoundOff] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [givenAmount, setGivenAmount] = useState(0);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    number: "+91 9876543210",
    name: "Kumaran T",
    address: "4517 Washington Ave, Manchester, Kentucky 39495",
  });

  // Sample billing data
  const billingData = [
    {
      id: 1,
      date: "5/8/25",
      billNo: "BIL-16415",
      contactNumber: " 9017258936",
      name: "Virat Kohli",
      transaction: "Paid",
      amount: "₹ 195.00",
    },
    {
      id: 2,
      date: "19/9/25",
      billNo: "BIL-25489",
      contactNumber: " 9081254112",
      name: "Rohit Sharma",
      transaction: "Pending",
      amount: "₹ 295.00",
    },
    {
      id: 3,
      date: "28/12/25",
      billNo: "BIL-65984",
      contactNumber: " 9519251623",
      name: "Yuvraj Singh",
      transaction: "Paid",
      amount: "₹ 985.00",
    },
    {
      id: 4,
      date: "6/11/25",
      billNo: "BIL-45780",
      contactNumber: " 9356201490",
      name: "Irfan Pathan",
      transaction: "Paid",
      amount: "₹ 19500.00",
    },
  ];

  // Sample product data for QR scanning simulation
  const productDatabase = [
    { code: "12345", name: "Parle-G 100g", rate: 10.0 },
    { code: "67890", name: "Dove Soap 50grm", rate: 15.0 },
    { code: "11111", name: "AA Rice", rate: 25.0 },
    { code: "22222", name: "Sunflower Oil 1lt", rate: 45.0 },
    { code: "33333", name: "Surf excel 500gm", rate: 35.0 },
  ];

  const paymentOptions = [
    { label: "Cash", value: "Cash" },
    { label: "Card", value: "Card" },
    { label: "UPI", value: "UPI" },
  ];

  // Calculate totals
  const calculateItemTotal = (item) => {
    const subtotal = item.qty * item.rate;
    const discountAmount = (subtotal * item.discount) / 100;
    return subtotal - discountAmount;
  };

  const getSubtotal = () => {
    return invoiceItems.reduce(
      (sum, item) => sum + calculateItemTotal(item),
      0
    );
  };

  const getTaxAmount = () => {
    return getSubtotal() * 0.05; // 5% tax
  };

  const getTotalAmount = () => {
    return getSubtotal() + getTaxAmount() + roundOff;
  };

  const getBalanceAmount = () => {
    return givenAmount - getTotalAmount();
  };

  // Generate round-off options based on current total
  const generateRoundOffOptions = () => {
    const total = getSubtotal() + getTaxAmount();
    const decimal = total - Math.floor(total);

    if (decimal === 0) {
      return [{ label: "₹ 0.00", value: 0 }];
    }

    const roundUp = 1 - decimal;
    const roundDown = -decimal;

    return [
      { label: "₹ 0.00", value: 0 },
      { label: `+₹ ${roundUp.toFixed(2)}`, value: roundUp },
      { label: `₹ ${roundDown.toFixed(2)}`, value: roundDown },
    ];
  };

  // Check if current item is valid for adding new line
  const isCurrentItemValid = () => {
    const lastItem = invoiceItems[invoiceItems.length - 1];
    return (
      lastItem.productName.trim() !== "" &&
      lastItem.qty > 0 &&
      lastItem.rate > 0
    );
  };

  // Calculate total savings (this would typically be based on discounts)
  const getTotalSavings = () => {
    return invoiceItems.reduce((savings, item) => {
      const originalPrice = item.qty * item.rate;
      const discountAmount = (originalPrice * item.discount) / 100;
      return savings + discountAmount;
    }, 0);
  };

  // Calculate points (example: 1 point per ₹10 spent)
  const getPoints = () => {
    return Math.floor(getTotalAmount() / 10);
  };

  // Add new product row
  const addProduct = () => {
    // Check if current items are valid before adding new
    if (!isCurrentItemValid()) {
      toastRef.current.show({
        severity: "warn", // success | info | warn | error
        summary: "Validation Error",
        detail:
          "Please fill in the product name, quantity, and rate for the current item before adding a new one.",
        life: 3000,
      });
      return;
    }

    const newId = Math.max(...invoiceItems.map((item) => item.id)) + 1;
    setInvoiceItems([
      ...invoiceItems,
      {
        id: newId,
        productName: "",
        qty: 0,
        rate: 0,
        discount: 0,
        total: 0,
      },
    ]);
  };

  // Delete product row
  const deleteProduct = (id) => {
    if (invoiceItems.length > 1) {
      setInvoiceItems(invoiceItems.filter((item) => item.id !== id));
    }
  };

  // Update item field
  const updateItem = (id, field, value) => {
    setInvoiceItems(
      invoiceItems.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]: value,
              total:
                field === "productName"
                  ? item.total
                  : calculateItemTotal({ ...item, [field]: value }),
            }
          : item
      )
    );
  };

  // Simulate QR scanning
  const startQRScan = () => {
    // Check if current items are valid before adding new via QR
    if (invoiceItems.length > 0 && !isCurrentItemValid()) {
      alert(
        "Please fill in the current item details before scanning a new product."
      );
      return;
    }

    setIsScanning(true);
    // Simulate QR scan delay
    setTimeout(() => {
      // Randomly select a product from database
      const randomProduct =
        productDatabase[Math.floor(Math.random() * productDatabase.length)];

      // Add scanned product
      const newId = Math.max(...invoiceItems.map((item) => item.id)) + 1;
      setInvoiceItems([
        ...invoiceItems,
        {
          id: newId,
          productName: randomProduct.name,
          qty: 1,
          rate: randomProduct.rate,
          discount: 0,
          total: randomProduct.rate,
        },
      ]);

      setIsScanning(false);
      alert(`Scanned: ${randomProduct.name} (Code: ${randomProduct.code})`);
    }, 2000);
  };

  // Transaction status template
  const transactionTemplate = (rowData) => {
    const severity = rowData.transaction === "Paid" ? "success" : "danger";
    return (
      <Tag
        value={rowData.transaction}
        severity={severity}
        style={{
          backgroundColor:
            rowData.transaction === "Paid" ? "#d4edda" : "#f8d7da",
          color: rowData.transaction === "Paid" ? "#155724" : "#721c24",
          border: "none",
          padding: "0.25rem 0.75rem",
          borderRadius: "0.375rem",
          fontSize: "0.875rem",
          fontWeight: "normal",
        }}
      />
    );
  };

  // Grid header
  const renderGridHeader = () => {
    return (
      <div
        className="my-2 align-items-center"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0.5rem",
          backgroundColor: currentView === "grid" ? "white" : "",
        }}
      >
        <h2 className="mb-0">Billing</h2>
        {currentView === "grid" ? <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <div style={{ position: "relative" }}>
            <i
              className="pi pi-search"
              style={{
                position: "absolute",
                left: "0.75rem",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#6c757d",
                fontSize: "0.875rem",
              }}
            />
            <InputText
              type="search"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search"
              style={{
                paddingLeft: "2.5rem",
                width: "250px",
                border: "1px solid #ced4da",
                borderRadius: "0.375rem",
                padding: "0.5rem 0.5rem 0.5rem 2.5rem",
              }}
            />
          </div>
          <Button
            label="+ Create New"
            className="btn btn-primary"
            onClick={() => setCurrentView("invoice")}
          />
        </div> : <Button
              icon="pi pi-arrow-left"
              label="Back to Billing"
              className="btn btn-danger"
              onClick={() => setCurrentView("grid")}
            />}
      </div>
    );
  };

  // Invoice header
  const renderInvoiceHeader = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h2
            style={{
              color: "#1976d2",
              margin: "0 0 1rem 0",
              fontSize: "1.5rem",
              fontWeight: "600",
            }}
          >
            Orange Department Store
          </h2>
          <div
            style={{ fontSize: "0.875rem", color: "#666", lineHeight: "1.5" }}
          >
            <div>Shop no 01, NO.07 PONDY CUDDALORE MAIN ROAD</div>
            <div>MARAPPIAM, MUDALIARPET, PONDICHERRY</div>
            <div>605 004</div>
          </div>
        </div>
        <div
          style={{
            textAlign: "right",
            fontSize: "0.875rem",
            color: "#666",
            lineHeight: "1.5",
          }}
        >
          <div>Phone Number: 9874563210</div>
          <div>GSTN: 2ADF212F12B0228I2</div>
          <div>Fssai No: 12515823253265265</div>
        </div>
      </div>
    );
  };

  if (currentView === "grid") {
    return (
      <>
        <div className="card card-borderless cc-greyCard-custm mb-3">
          {renderGridHeader()}
        </div>
        <div
          style={{
            backgroundColor: "#f8f9fa",
            minHeight: "100vh",
           
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "0.5rem",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              padding: "1.5rem",
            }}
          >

            <DataTable
              value={billingData}
              globalFilter={globalFilter}
              paginator={false}
              stripedRows
              style={{ border: "none" }}
              tableStyle={{ minWidth: "100%", border: "none" }}
              pt={{
                header: {
                  style: {
                    backgroundColor: "#f8f9fa",
                    border: "none",
                    borderBottom: "2px solid #dee2e6",
                  },
                },
                headerRow: { style: { backgroundColor: "#f8f9fa" } },
                headerCell: {
                  style: {
                    backgroundColor: "#f8f9fa",
                    color: "#495057",
                    fontWeight: "600",
                    fontSize: "0.875rem",
                    textTransform: "none",
                    padding: "0.75rem",
                    border: "none",
                    borderBottom: "2px solid #dee2e6",
                  },
                },
                bodyRow: { style: { borderBottom: "1px solid #e9ecef" } },
                bodyCell: {
                  style: {
                    padding: "0.75rem",
                    fontSize: "0.875rem",
                    color: "#495057",
                    border: "none",
                  },
                },
              }}
            >
              <Column field="date" header="Date" style={{ width: "10%" }} />
              <Column
                field="billNo"
                header="Bill No"
                style={{ width: "15%" }}
              />
              <Column
                field="contactNumber"
                header="Contact Number"
                style={{ width: "20%" }}
              />
              <Column field="name" header="Name" style={{ width: "15%" }} />
              <Column
                field="transaction"
                header="Transaction"
                body={transactionTemplate}
                style={{ width: "15%" }}
              />
              <Column
                field="amount"
                header="Amount"
                style={{ width: "15%", textAlign: "right" }}
              />
            </DataTable>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toast ref={toastRef} />

      <div className="card card-borderless cc-greyCard-custm mb-3 d-flex justify-content-between">
        <div>{renderGridHeader()}</div>
         <div >
            
          </div>
      </div>

        <div
          style={{
            backgroundColor: "white",
            borderRadius: "0.5rem",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            padding: "1rem",
          }}
        >
         

          {renderInvoiceHeader()}

          <div style={{ display: "flex", gap: "2rem" }}>
            {/* Product Entry Section */}
            <div style={{ flex: "2" }}>
              {/* Product Entry Header with Buttons */}
              <div
                style={{
                  color: "white",
                  padding: "0.75rem",
                  margin: "0 0 1rem 0",
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                  borderRadius: "0.375rem",
                }}
              >
                {/* Left side heading */}
                {/* <h3 style={{ margin: 0 }}>Product Entry Section</h3> */}

                {/* Right side buttons */}
                <div style={{ display: "flex", gap: "1rem" }}>
                  <Button
                    icon="pi pi-plus"
                    label="Add Product"
                    onClick={addProduct}
                    style={{
                      backgroundColor: "#ffc107",
                      border: "1px solid #ffc107",
                      color: "#000",
                      borderRadius: "0.375rem",
                    }}
                  />
                  <Button
                    icon={isScanning ? "pi pi-spin pi-spinner" : "pi pi-qrcode"}
                    label={isScanning ? "Scanning..." : "Scan QR Code"}
                    onClick={startQRScan}
                    disabled={isScanning}
                    style={{
                      backgroundColor: "#28a745",
                      border: "1px solid #28a745",
                      color: "white",
                      borderRadius: "0.375rem",
                    }}
                  />
                </div>
              </div>

              {/* Product Table */}
              <div
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "0.375rem",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "60px 1fr minmax(80px,100px) minmax(80px,100px) minmax(80px,100px) minmax(100px,120px) 60px",
                    backgroundColor: "#1E6444",
                    color: "white",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  <div style={{ padding: "0.75rem", textAlign: "center" }}>
                    #
                  </div>
                  <div style={{ padding: "0.75rem" }}>Product Name</div>
                  <div style={{ padding: "0.75rem", textAlign: "center" }}>
                    Qty
                  </div>
                  <div style={{ padding: "0.75rem", textAlign: "center" }}>
                    Rate ₹
                  </div>
                  <div style={{ padding: "0.75rem", textAlign: "center" }}>
                    Discount %
                  </div>
                  <div style={{ padding: "0.75rem", textAlign: "center" }}>
                    Total ₹
                  </div>
                  <div
                    style={{ padding: "0.75rem", textAlign: "center" }}
                  ></div>
                </div>

                {invoiceItems.map((item, index) => (
                  <div
                    key={item.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "60px 1fr minmax(80px,100px) minmax(80px,100px) minmax(80px,100px) minmax(100px,120px) 60px",
                      borderBottom:
                        index < invoiceItems.length - 1
                          ? "1px solid #dee2e6"
                          : "none",
                      backgroundColor: index % 2 === 0 ? "#f8f9fa" : "white",
                    }}
                  >
                    <div
                      style={{
                        padding: "0.5rem",
                        textAlign: "center",
                        alignSelf: "center",
                      }}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </div>
                    <div style={{ padding: "0.5rem" }}>
                      <InputText
                        value={item.productName}
                        onChange={(e) =>
                          updateItem(item.id, "productName", e.target.value)
                        }
                        placeholder="Enter product name"
                        style={{
                          width: "100%",
                          border: "1px solid #ced4da",
                          borderRadius: "0.25rem",
                        }}
                      />
                    </div>
                    <div style={{ padding: "0.5rem" }}>
                      <InputNumber
                        value={item.qty}
                        onValueChange={(e) =>
                          updateItem(item.id, "qty", e.value)
                        }
                        min={0}
                        style={{ width: "100%", boxSizing: "border-box" }}
                      />
                    </div>
                    <div style={{ padding: "0.5rem" }}>
                      <InputNumber
                        value={item.rate}
                        onValueChange={(e) =>
                          updateItem(item.id, "rate", e.value)
                        }
                        min={0}
                        minFractionDigits={2}
                        style={{ width: "100%", boxSizing: "border-box" }}
                      />
                    </div>
                    <div style={{ padding: "0.5rem" }}>
                      <InputNumber
                        value={item.discount}
                        onValueChange={(e) =>
                          updateItem(item.id, "discount", e.value)
                        }
                        min={0}
                        max={100}
                        style={{ width: "100%", boxSizing: "border-box" }}
                      />
                    </div>
                    <div
                      style={{
                        padding: "0.5rem",
                        textAlign: "center",
                        alignSelf: "center",
                        fontWeight: "500",
                      }}
                    >
                      {calculateItemTotal(item).toFixed(2)}
                    </div>
                    <div style={{ padding: "0.5rem", textAlign: "center" }}>
                      {invoiceItems.length > 1 && (
                        <Button
                          icon="pi pi-trash"
                          className="p-button-danger p-button-sm p-button-text"
                          onClick={() => deleteProduct(item.id)}
                          style={{ padding: "0.25rem" }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div
                className="d-flex justify-content-end"
                style={{ marginTop: "2rem", gap: "1rem" }}
              >
                <Button
                  icon="pi pi-save"
                  label="Save"
                  className="btn btn-primary"
                />
                <Button
                  icon="pi pi-print"
                  label="Print Bill"
                  style={{
                    backgroundColor: "#6c757d",
                    border: "1px solid #6c757d",
                    color: "white",
                  }}
                  onClick={() => setShowPrintModal(true)}
                />
                <Button
                  // icon="pi pi-times"
                  label="Cancel"
                  className="p-button-danger"
                  onClick={() => setCurrentView("grid")}
                />
              </div>
            </div>

            {/* Summary Section */}
            <div style={{ flex: "1" }}>
              <h3
                style={{
                  backgroundColor: "#1E6444",
                  color: "white",
                  padding: "0.75rem",
                  margin: "0 0 1rem 0",
                }}
              >
                Summary Section
              </h3>

              <div
                style={{
                  padding: "1rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "0.375rem",
                }}
              >
                <div style={{ marginBottom: "1rem" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span>Subtotal:</span>
                    <span>₹ {getSubtotal().toFixed(2)}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span>Tax (e.g., GST 5%):</span>
                    <span>₹ {getTaxAmount().toFixed(2)}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span>Round Off:</span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <i
                        className="pi pi-pencil"
                        style={{ fontSize: "0.75rem" }}
                      />
                      <Dropdown
                        value={roundOff}
                        options={generateRoundOffOptions()}
                        onChange={(e) => setRoundOff(e.value)}
                        style={{ width: "120px", fontSize: "0.75rem" }}
                        placeholder="Select"
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontWeight: "bold",
                      borderTop: "1px solid #dee2e6",
                      paddingTop: "0.5rem",
                      marginTop: "1rem",
                    }}
                  >
                    <span>Total Amount:</span>
                    <span>₹ {getTotalAmount().toFixed(2)}</span>
                  </div>
                </div>

                <div style={{ marginBottom: "2rem" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: "500",
                    }}
                  >
                    Payment Type:
                  </label>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    {paymentOptions.map((option) => (
                      <Button
                        key={option.value}
                        label={option.label}
                        onClick={() => setPaymentType(option.value)}
                        style={{
                          backgroundColor:
                            paymentType === option.value
                              ? "#1976d2"
                              : "transparent",
                          color:
                            paymentType === option.value ? "white" : "#1976d2",
                          border: "1px solid #1976d2",
                          borderRadius: "0.375rem",
                          padding: "0.5rem 1rem",
                          fontSize: "0.875rem",
                        }}
                      />
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #dee2e6",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.5rem",
                      fontSize: "1rem",
                      fontWeight: "bold",
                    }}
                  >
                    <span>TOTAL AMOUNT</span>
                    <span>₹ {getTotalAmount().toFixed(2)}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span>GIVEN AMOUNT</span>
                    <InputNumber
                      value={givenAmount}
                      onValueChange={(e) => setGivenAmount(e.value || 0)}
                      minFractionDigits={2}
                      placeholder="0.00"
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      color: getBalanceAmount() < 0 ? "#dc3545" : "#28a745",
                      fontWeight: "bold",
                      borderTop: "1px solid #dee2e6",
                      paddingTop: "0.5rem",
                    }}
                  >
                    <span>BALANCE AMOUNT</span>
                    <span>₹ {getBalanceAmount().toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Print Bill Modal */}
          <Dialog
            visible={showPrintModal}
            onHide={() => setShowPrintModal(false)}
            header=""
            modal
            style={{ width: "500px" }}
            contentStyle={{ padding: "0" }}
            headerStyle={{ display: "none" }}
          >
            <div style={{ padding: "0" }}>
              {/* Summary Section Header */}
              <div
                style={{
                  backgroundColor: "#1976d2",
                  color: "white",
                  padding: "1rem",
                  textAlign: "center",
                  fontWeight: "500",
                  fontSize: "1.1rem",
                }}
              >
                Summary Section
              </div>

              <div style={{ padding: "1.5rem" }}>
                {/* Financial Summary */}
                <div style={{ marginBottom: "1.5rem" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.75rem",
                      fontSize: "0.95rem",
                    }}
                  >
                    <span style={{ color: "#666" }}>Subtotal:</span>
                    <span style={{ fontWeight: "500" }}>
                      ₹ {getSubtotal().toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.75rem",
                      fontSize: "0.95rem",
                    }}
                  >
                    <span style={{ color: "#666" }}>Tax (e.g., GST 5%):</span>
                    <span style={{ fontWeight: "500" }}>
                      ₹ {getTaxAmount().toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.75rem",
                      fontSize: "0.95rem",
                    }}
                  >
                    <span style={{ color: "#666" }}>Total Savings:</span>
                    <span style={{ fontWeight: "500", color: "#28a745" }}>
                      ₹ {getTotalSavings().toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "1rem",
                      fontSize: "0.95rem",
                    }}
                  >
                    <span style={{ color: "#666" }}>Points:</span>
                    <span style={{ fontWeight: "500" }}>
                      {getPoints().toFixed(2)}
                    </span>
                  </div>

                  {/* Payment Type */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <span
                      style={{
                        color: "#666",
                        marginRight: "1rem",
                        fontSize: "0.95rem",
                      }}
                    >
                      Change Payment:
                    </span>
                    <div
                      style={{
                        display: "inline-flex",
                        gap: "0.5rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      {paymentOptions.map((option) => (
                        <Button
                          key={option.value}
                          label={option.label}
                          size="small"
                          style={{
                            backgroundColor:
                              paymentType === option.value
                                ? "#1976d2"
                                : "transparent",
                            color:
                              paymentType === option.value
                                ? "white"
                                : "#1976d2",
                            border: "1px solid #1976d2",
                            borderRadius: "0.375rem",
                            padding: "0.4rem 0.8rem",
                            fontSize: "0.85rem",
                          }}
                          onClick={() => setPaymentType(option.value)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Amount Details */}
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "1rem",
                      borderRadius: "0.5rem",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "0.75rem",
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                      }}
                    >
                      <span>TOTAL AMOUNT</span>
                      <span>{Math.round(getTotalAmount())}</span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <span style={{ color: "#666" }}>ROUND OFF</span>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <span style={{ fontWeight: "500" }}>
                          ₹ {(getSubtotal() + getTaxAmount()).toFixed(2)}
                        </span>
                        <span style={{ fontWeight: "500", color: "#1976d2" }}>
                          ₹ {Math.round(getTotalAmount())}
                        </span>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <span style={{ color: "#666" }}>ACTUAL AMOUNT</span>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <span style={{ fontWeight: "500", color: "#1976d2" }}>
                          ₹ {Math.round(givenAmount)}
                        </span>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontWeight: "bold",
                        borderTop: "1px solid #dee2e6",
                        paddingTop: "0.75rem",
                      }}
                    >
                      <span>BALANCE AMOUNT</span>
                      <span>{Math.round(getBalanceAmount())}</span>
                    </div>
                  </div>

                  {/* Customer Details */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "1rem",
                        paddingBottom: "0.5rem",
                        borderBottom: "1px solid #e9ecef",
                      }}
                    >
                      <span style={{ fontWeight: "500", color: "#333" }}>
                        Customer Detail
                      </span>
                      <Button
                        label="+ Add Customer"
                        size="small"
                        style={{
                          backgroundColor: "#e3f2fd",
                          color: "#1976d2",
                          border: "1px solid #bbdefb",
                          borderRadius: "0.375rem",
                          padding: "0.4rem 0.8rem",
                          fontSize: "0.85rem",
                        }}
                      />
                    </div>

                    <div style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
                      <div style={{ marginBottom: "0.5rem" }}>
                        <span style={{ color: "#666" }}>Customer Number: </span>
                        <span style={{ fontWeight: "500" }}>
                          {customerDetails.number}
                        </span>
                      </div>

                      <div style={{ marginTop: "1rem" }}>
                        <div
                          style={{ fontWeight: "500", marginBottom: "0.25rem" }}
                        >
                          {customerDetails.name}
                        </div>
                        <div style={{ color: "#666", fontSize: "0.9rem" }}>
                          {customerDetails.address}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Modal Action Buttons */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "1rem",
                      paddingTop: "1rem",
                      borderTop: "1px solid #e9ecef",
                    }}
                  >
                    <Button
                      icon="pi pi-print"
                      label="Print Bill"
                      style={{
                        backgroundColor: "#1976d2",
                        border: "1px solid #1976d2",
                        color: "white",
                        borderRadius: "0.375rem",
                        padding: "0.5rem 1rem",
                      }}
                      onClick={() => {
                        // Handle print functionality here
                        alert("Printing bill...");
                        setShowPrintModal(false);
                      }}
                    />
                    <Button
                      label="Cancel"
                      className="p-button-secondary"
                      style={{
                        border: "1px solid #6c757d",
                        color: "#6c757d",
                        backgroundColor: "transparent",
                        borderRadius: "0.375rem",
                        padding: "0.5rem 1rem",
                      }}
                      onClick={() => setShowPrintModal(false)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Dialog>
        </div>
    </>
  );
};
export default Billing;
